import React, { useState } from "react";
import { useUpdateAiAgent } from "../../../hooks/useAiAgents";
import {
  useToast,
  useFranchisesInfinite,
  useFranchise,
  usePostHog,
} from "@finitless/shared/hooks";
import { handleApiError, handleApiSuccess } from "@finitless/shared/lib";
import { useTranslation } from "react-i18next";
import ProviderSettingsForm from "./ProviderSettingsForm";
import FranchiseSelect from "./FranchiseSelect";
import AgentSitesSection from "./AgentSitesSection";
import AgentTemplatesModal from "./AgentTemplatesModal";
import Modal from "../../ui/UIElements/Modal";
import FranchiseChangeConfirmationModal from "../../ui/Modals/FranchiseChangeConfirmationModal";
import { ContextHelp } from "../../ui";
import { convertTime } from "../../../lib/globalUtils";
import { useUiLocale } from "../../../hooks/useUiLocale";
import { resolveAgentSettingLabel } from "../../../lib/aiAgentI18n";
import {
  normalizeLanguageCode,
  normalizeSettingsLanguage,
  LANGUAGE_OPTIONS,
} from "../../../lib/aiAgentLanguage";
import { useIntercomContext } from "@finitless/shared/contexts";

const AgentDetailModal = ({
  agent,
  isOpen,
  onClose,
  autoConnectOAuth = false,
}) => {
  const { t } = useTranslation("aiAgents");
  const { locale } = useUiLocale();
  // Start in edit mode if autoConnectOAuth is true
  const [isEditing, setIsEditing] = useState(autoConnectOAuth);
  const [editedSettings, setEditedSettings] = useState({});
  const [editedFranchiseId, setEditedFranchiseId] = useState(
    String(agent?.franchise_id || 1),
  );

  // Track original values to detect changes
  const [originalSettings, setOriginalSettings] = useState({});
  const [originalFranchiseId, setOriginalFranchiseId] = useState(
    String(agent?.franchise_id || 1),
  );

  // State for franchise change confirmation
  const [showFranchiseConfirmation, setShowFranchiseConfirmation] =
    useState(false);
  const [pendingUpdateData, setPendingUpdateData] = useState(null);
  const [showTemplatesModal, setShowTemplatesModal] = useState(false);

  // Fetch detailed agent data (for future use)
  // const { data: detailedAgent, isLoading: agentLoading } = useAiAgent(agent.id);

  // Fetch conversation data (for future use)
  // const { data: conversation } = useAgentCurrentConversation(agent.provider_external_id);
  // const { data: messages } = useAgentConversationMessages(agent.provider_external_id);

  // Hooks
  const updateAgentMutation = useUpdateAiAgent();
  const {
    franchises,
    isLoading: franchisesLoading,
    isFetchingNextPage: franchisesFetchingNextPage,
    hasNextPage: franchisesHasNextPage,
    fetchNextPage: fetchNextFranchisesPage,
  } = useFranchisesInfinite({ enabled: !!isOpen });

  const agentFranchiseFromList = agent
    ? franchises.find((f) => f.id === agent.franchise_id)
    : undefined;
  const { data: agentFranchiseFallback } = useFranchise(
    isOpen && agent?.franchise_id && !agentFranchiseFromList
      ? agent.franchise_id
      : null,
  );
  const agentFranchiseDisplayName =
    agentFranchiseFromList?.name ?? agentFranchiseFallback?.name ?? "-";

  const origIdNum = Number.parseInt(originalFranchiseId, 10);
  const newIdNum = Number.parseInt(editedFranchiseId, 10);
  const origFranchiseInList = franchises.find((f) => f.id === origIdNum);
  const newFranchiseInList = franchises.find((f) => f.id === newIdNum);
  const { data: confirmOrigFranchiseFallback } = useFranchise(
    showFranchiseConfirmation &&
      !origFranchiseInList &&
      Number.isFinite(origIdNum)
      ? origIdNum
      : null,
  );
  const { data: confirmNewFranchiseFallback } = useFranchise(
    showFranchiseConfirmation &&
      !newFranchiseInList &&
      Number.isFinite(newIdNum)
      ? newIdNum
      : null,
  );
  const currentFranchiseConfirmName =
    origFranchiseInList?.name ??
    confirmOrigFranchiseFallback?.name ??
    t("common.unknown");
  const newFranchiseConfirmName =
    newFranchiseInList?.name ??
    confirmNewFranchiseFallback?.name ??
    t("common.unknown");

  const toast = useToast();
  const { trackActivation } = usePostHog();
  const { showMessage, isEnabled: isIntercomEnabled } = useIntercomContext();

  // Check if this is a WhatsApp agent with business_login auth
  const isWhatsApp = agent?.provider === "whatsapp";
  const isBusinessLogin =
    editedSettings?.auth_type === "business_login" ||
    agent?.provider_settings?.auth_type === "business_login";
  const showLanguageInModal = isWhatsApp && isBusinessLogin;

  // Handle language code change
  const handleLanguageChange = (e) => {
    const normalizedValue = normalizeLanguageCode(e.target.value);
    setEditedSettings((prev) => ({
      ...prev,
      language_code: normalizedValue,
    }));
  };

  // Initialize edited settings when agent changes
  React.useEffect(() => {
    if (agent) {
      const settings = normalizeSettingsLanguage(
        agent.provider,
        agent.provider_settings,
      );
      const franchiseId = String(agent.franchise_id || 1);

      setEditedSettings({
        ...settings,
        ...(agent.provider === "whatsapp"
          ? { provider_external_id: agent.provider_external_id || "" }
          : {}),
      });
      setEditedFranchiseId(franchiseId);

      // Store original values for change detection
      setOriginalSettings({
        ...settings,
        ...(agent.provider === "whatsapp"
          ? { provider_external_id: agent.provider_external_id || "" }
          : {}),
      });
      setOriginalFranchiseId(franchiseId);
    }
  }, [agent]);

  const getProviderName = (provider) => {
    switch (provider) {
      case "whatsapp":
        return t("providers.whatsapp");
      case "phone":
        return t("providers.phone");
      case "web":
        return t("providers.web");
      default:
        return t("providers.default");
    }
  };

  const getProviderDescription = (provider) => {
    switch (provider) {
      case "whatsapp":
        return t("providerDescriptions.whatsapp");
      case "phone":
        return t("providerDescriptions.phone");
      case "web":
        return t("providerDescriptions.web");
      default:
        return t("providerDescriptions.default");
    }
  };

  const truncateValue = (key, value) => {
    // Special handling for access tokens and other sensitive/long values
    if (
      key.toLowerCase().includes("token") ||
      key.toLowerCase().includes("key") ||
      key.toLowerCase().includes("secret")
    ) {
      if (typeof value === "string" && value.length > 20) {
        return `${value.substring(0, 8)}...${value.substring(
          value.length - 8,
        )}`;
      }
    }

    // General truncation for very long strings
    if (typeof value === "string" && value.length > 50) {
      return `${value.substring(0, 47)}...`;
    }

    return value;
  };

  const handleFranchiseSelect = (franchiseId) => {
    setEditedFranchiseId(franchiseId);
  };

  // Helper function to detect changes in provider settings
  const getChangedSettings = () => {
    const changedSettings = {};

    // Check each setting for changes
    Object.keys(editedSettings).forEach((key) => {
      if (editedSettings[key] !== originalSettings[key]) {
        changedSettings[key] = editedSettings[key];
      }
    });

    // Check for new settings that weren't in original
    Object.keys(editedSettings).forEach((key) => {
      if (!(key in originalSettings) && editedSettings[key] !== "") {
        changedSettings[key] = editedSettings[key];
      }
    });

    return changedSettings;
  };

  // Helper function to check if there are any changes
  const hasChanges = () => {
    const changedSettings = getChangedSettings();
    const franchiseIdChanged = editedFranchiseId !== originalFranchiseId;

    return Object.keys(changedSettings).length > 0 || franchiseIdChanged;
  };

  // Helper function to build the update data payload
  const buildUpdatePayload = () => {
    if (!hasChanges()) {
      return null;
    }

    const updateData = {};
    const changedSettings = getChangedSettings();

    // auth_type can only be sent when changing to "manual"
    if (changedSettings.auth_type && changedSettings.auth_type !== "manual") {
      delete changedSettings.auth_type;
    }

    // Track if WhatsApp external_id was added for analytics
    const shouldTrackWhatsAppConnection =
      agent.provider === "whatsapp" &&
      editedSettings.provider_external_id &&
      !originalSettings.provider_external_id;

    // Handle provider_external_id changes
    if (
      Object.prototype.hasOwnProperty.call(
        changedSettings,
        "provider_external_id",
      ) &&
      editedSettings.provider_external_id !==
        originalSettings.provider_external_id
    ) {
      updateData.provider_external_id = editedSettings.provider_external_id;
      delete changedSettings.provider_external_id;
    }

    if (Object.keys(changedSettings).length > 0) {
      if (
        Object.prototype.hasOwnProperty.call(changedSettings, "language_code")
      ) {
        changedSettings.language_code = normalizeLanguageCode(
          changedSettings.language_code,
        );
      }
      updateData.provider_settings = changedSettings;
    }

    // Include franchise_id if it changed
    if (editedFranchiseId !== originalFranchiseId) {
      updateData.franchise_id = parseInt(editedFranchiseId) || 1;
    }

    return {
      updateData,
      shouldTrackWhatsAppConnection,
    };
  };

  // Save current changes without closing modal (used before OAuth connect)
  const saveBeforeConnect = async () => {
    const payload = buildUpdatePayload();
    if (!payload) return true; // No changes to save, proceed

    const { updateData, shouldTrackWhatsAppConnection } = payload;

    try {
      await updateAgentMutation.mutateAsync({
        agentId: agent.id,
        ...updateData,
      });

      if (shouldTrackWhatsAppConnection) {
        trackActivation.whatsappConnected({
          agent_id: agent.id,
          provider_external_id: editedSettings.provider_external_id,
          franchise_id: agent.franchise_id,
        });
      }

      handleApiSuccess(t("detail.toasts.updateSuccess"), toast, {
        title: t("common.success"),
      });

      // Update original values to reflect the saved state
      setOriginalSettings(editedSettings);
      setOriginalFranchiseId(editedFranchiseId);

      return true;
    } catch (error) {
      handleApiError(error, toast, {
        title: t("detail.toasts.updateErrorTitle"),
        defaultMessage: t("detail.toasts.updateErrorMessage"),
      });
      return false;
    }
  };

  // Main save handler - checks if franchise changed and shows confirmation
  const handleSaveChanges = async () => {
    try {
      const payload = buildUpdatePayload();

      if (!payload) {
        setIsEditing(false);
        return;
      }

      const { updateData, shouldTrackWhatsAppConnection } = payload;

      // Check if franchise_id is being changed
      const franchiseIdChanged = editedFranchiseId !== originalFranchiseId;

      if (franchiseIdChanged) {
        // Store pending changes and show confirmation modal
        setPendingUpdateData({
          updateData,
          shouldTrackWhatsAppConnection,
        });
        setShowFranchiseConfirmation(true);
        return; // Wait for user confirmation
      }

      // No franchise change, proceed directly
      await executeSave(updateData, shouldTrackWhatsAppConnection);
    } catch (error) {
      handleApiError(error, toast, {
        title: t("detail.toasts.updateErrorTitle"),
        defaultMessage: t("detail.toasts.updateErrorMessage"),
      });
    }
  };

  // Executes the actual API call
  const executeSave = async (updateData, shouldTrackWhatsAppConnection) => {
    try {
      await updateAgentMutation.mutateAsync({
        agentId: agent.id,
        ...updateData,
      });

      // Track WhatsApp connection AFTER successful mutation
      if (shouldTrackWhatsAppConnection) {
        trackActivation.whatsappConnected({
          agent_id: agent.id,
          provider_external_id: editedSettings.provider_external_id,
          franchise_id: agent.franchise_id,
        });
      }

      handleApiSuccess(t("detail.toasts.updateSuccess"), toast, {
        title: t("common.success"),
      });

      // Update original values to reflect the saved state
      setOriginalSettings(editedSettings);
      setOriginalFranchiseId(editedFranchiseId);

      setIsEditing(false);
      onClose?.();
    } catch (error) {
      handleApiError(error, toast, {
        title: t("detail.toasts.updateErrorTitle"),
        defaultMessage: t("detail.toasts.updateErrorMessage"),
      });
    }
  };

  // Handler for franchise change confirmation
  const handleFranchiseChangeConfirmed = async () => {
    if (!pendingUpdateData) return;

    const { updateData, shouldTrackWhatsAppConnection } = pendingUpdateData;

    // Close confirmation modal
    setShowFranchiseConfirmation(false);

    // Execute the save
    await executeSave(updateData, shouldTrackWhatsAppConnection);

    // Clear pending data
    setPendingUpdateData(null);
  };

  // Handler for franchise change cancellation
  const handleFranchiseChangeCancelled = () => {
    setShowFranchiseConfirmation(false);
    setPendingUpdateData(null);
    // Stay in edit mode - don't close the form
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={
        <div className="flex items-center justify-between">
          <div className="flex items-center justify-center space-x-3">
            <h1 className="font-semibold text-white">
              {getProviderName(agent.provider)}
            </h1>
            <div className="flex items-center justify-center gap-1">
              {(() => {
                const isEnabled = !!agent.enabled;
                const isConfigured = !!agent.configured;
                const reason = agent.misconfigured_reason;

                if (!isEnabled) {
                  return (
                    <>
                      <span className="rounded-full border border-red-500/30 bg-red-500/20 px-3 py-1 text-sm font-medium text-red-400">
                        {t("status.inactive")}
                      </span>
                      <ContextHelp
                        className="h-3.5 w-3.5"
                        ariaLabel={t("help.status.ariaLabel")}
                        whatIs={t("help.status.whatIs")}
                        note={t("help.status.inactive")}
                      />
                    </>
                  );
                }
                if (isConfigured) {
                  return (
                    <>
                      <span className="rounded-full border border-green-500/30 bg-green-500/20 px-3 py-1 text-sm font-medium text-green-400">
                        {t("status.active")}
                      </span>
                      <ContextHelp
                        className="h-3.5 w-3.5"
                        ariaLabel={t("help.status.ariaLabel")}
                        whatIs={t("help.status.whatIs")}
                        note={t("help.status.activeConfigured")}
                      />
                    </>
                  );
                }
                // Enabled but not configured
                let reasonText = null;
                if (reason) {
                  const reasonKey = `whatsappOAuth.errors.${reason}`;
                  const translatedReason = t(reasonKey);
                  reasonText =
                    translatedReason !== reasonKey ? translatedReason : reason;
                }
                return (
                  <>
                    <span className="rounded-full border border-yellow-500/30 bg-yellow-500/20 px-3 py-1 text-sm font-medium text-yellow-400">
                      {t("status.active")}
                    </span>
                    <ContextHelp
                      className="h-3.5 w-3.5"
                      ariaLabel={t("help.status.ariaLabel")}
                      whatIs={t("help.status.whatIs")}
                      note={reasonText || t("help.status.notConfigured")}
                      noteClass="text-red-400"
                    />
                  </>
                );
              })()}
            </div>
          </div>
        </div>
      }
      subtitle={getProviderDescription(agent.provider)}
      maxWidth="max-w-4xl"
    >
      <div className="space-y-8">
        {/* Agent Metrics */}
        {agent.metrics && (
          <div>
            <h3 className="mb-4 text-lg font-semibold text-white">
              {t("detail.metrics.title")}
            </h3>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div className="rounded-lg border border-gray-700 bg-gray-800 p-4">
                <div className="text-center">
                  <div className="mb-1 text-2xl font-bold text-green-400">
                    {agent.metrics.success_rate || 0}%
                  </div>
                  <div className="flex items-center justify-center gap-1 text-sm text-gray-400">
                    {t("metrics.successRate")}
                    <ContextHelp
                      ariaLabel={t("help.metrics.successRate.ariaLabel")}
                      whatIs={t("help.metrics.successRate.whatIs")}
                      formula={t("help.metrics.successRate.formula")}
                      period={t("help.metrics.successRate.period")}
                    />
                  </div>
                </div>
              </div>
              <div className="rounded-lg border border-gray-700 bg-gray-800 p-4">
                <div className="text-center">
                  <div className="mb-1 text-2xl font-bold text-blue-400">
                    {convertTime(
                      agent.metrics.average_response_time_ms || 0,
                      "ms",
                      "s",
                      { decimals: 1 },
                    )}
                  </div>
                  <div className="flex items-center justify-center gap-1 text-sm text-gray-400">
                    {t("metrics.avgResponseTime")}
                    <ContextHelp
                      ariaLabel={t("help.metrics.avgResponseTime.ariaLabel")}
                      whatIs={t("help.metrics.avgResponseTime.whatIs")}
                      period={t("help.metrics.avgResponseTime.period")}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Sites & Domains - Only for finitless/chat agents */}
        {agent.provider === "finitless" && agent.provider_type === "chat" && (
          <AgentSitesSection agent={agent} isAgentEnabled={!!agent.enabled} />
        )}

        {/* Configuration */}
        <div>
          <div className="mb-4 flex items-center justify-between">
            <h3 className="text-lg font-semibold text-white">
              {t("detail.configuration.title")}
            </h3>
          </div>

          {isEditing ? (
            <div className="space-y-6">
              {/* Brand Selector */}
              <div>
                <label className="mb-2 block text-sm font-medium text-gray-300">
                  {t("detail.configuration.franchise")}
                </label>
                <FranchiseSelect
                  value={editedFranchiseId}
                  onChange={handleFranchiseSelect}
                  disabled={franchisesLoading}
                  placeholder={t("detail.configuration.selectFranchise")}
                  placeholderLoading={t(
                    "detail.configuration.loadingFranchises",
                  )}
                  loadingMoreLabel={t(
                    "createModal.placeholders.loadingMoreFranchises",
                  )}
                  franchises={franchises}
                  isLoading={franchisesLoading}
                  isFetchingNextPage={franchisesFetchingNextPage}
                  hasNextPage={franchisesHasNextPage}
                  fetchNextPage={fetchNextFranchisesPage}
                  disabledSuffix={t("common.disabledSuffix")}
                  required
                  aria-label={t("detail.configuration.franchise")}
                />
              </div>

              {/* Agent Default Language - shown in modal for WhatsApp business_login */}
              {showLanguageInModal && (
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-300">
                    {t("settingsForm.languageTitle")}
                    <span className="ml-1 text-red-400">*</span>
                  </label>
                  <select
                    value={normalizeLanguageCode(
                      editedSettings?.language_code || "",
                    )}
                    onChange={handleLanguageChange}
                    className="custom-select w-full rounded-md border border-gray-600 bg-gray-800 px-3 py-2 text-white focus:ring-1 focus:ring-blue-500 focus:outline-none"
                    required
                  >
                    {LANGUAGE_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {t(`settingsForm.languageOptions.${option.value}`)}
                      </option>
                    ))}
                  </select>
                  {isIntercomEnabled && (
                    <button
                      type="button"
                      onClick={() =>
                        showMessage(
                          t("settingsForm.newLanguageIntercomPrefill"),
                        )
                      }
                      className="mt-1 text-sm text-blue-400 transition-colors hover:text-blue-300 hover:underline"
                    >
                      {t("settingsForm.newLanguageCta")}
                    </button>
                  )}
                </div>
              )}

              {/* Provider Settings Form */}
              <ProviderSettingsForm
                provider={agent.provider}
                type={agent.provider_type || "chat"}
                initialValues={editedSettings}
                onChange={setEditedSettings}
                readOnly={false}
                agent={agent}
                autoConnectOAuth={autoConnectOAuth}
                onSaveBeforeConnect={saveBeforeConnect}
              />
            </div>
          ) : (
            <div className="space-y-4">
              {(() => {
                // Fields to hide in view mode
                const hiddenFields = [
                  "access_token",
                  "meta_user_id",
                  "connected_at",
                  "waba_id",
                  "business_id",
                  "oauth_state",
                ];

                // Get configured display info
                const getConfiguredInfo = () => {
                  if (agent.configured) {
                    return { value: t("common:states.yes"), reason: null };
                  }
                  const reason = agent.misconfigured_reason;
                  if (reason) {
                    const errorKey = `whatsappOAuth.errors.${reason}`;
                    const translatedReason = t(errorKey);
                    const displayReason =
                      translatedReason !== errorKey ? translatedReason : reason;
                    return {
                      value: t("common:states.no"),
                      reason: displayReason,
                    };
                  }
                  return { value: t("common:states.no"), reason: null };
                };

                // Translate auth_type value for display
                const getAuthTypeLabel = (authType) => {
                  if (authType === "business_login") {
                    return t("whatsappOAuth.authTypeBusinessLogin");
                  }
                  if (authType === "manual") {
                    return t("whatsappOAuth.authTypeManual");
                  }
                  return authType;
                };

                const configuredInfo = getConfiguredInfo();

                // Transform provider_settings for display
                const transformedSettings = { ...agent.provider_settings };
                if (transformedSettings.auth_type) {
                  transformedSettings.auth_type = getAuthTypeLabel(
                    transformedSettings.auth_type,
                  );
                }
                if (!isWhatsApp) {
                  delete transformedSettings.pin_downloaded_at;
                } else if (!transformedSettings.pin_downloaded_at) {
                  transformedSettings.pin_downloaded_at = t(
                    "whatsappOAuth.info.notDownloaded",
                  );
                }
                if (transformedSettings.registration_status) {
                  const status =
                    transformedSettings.registration_status.toUpperCase();
                  if (status === "CONNECTED") {
                    transformedSettings.registration_status = t(
                      "whatsappOAuth.registrationStatus.connected",
                    );
                  } else if (status === "FLAGGED") {
                    transformedSettings.registration_status = t(
                      "whatsappOAuth.registrationStatus.flagged",
                    );
                  } else if (status === "RESTRICTED") {
                    transformedSettings.registration_status = t(
                      "whatsappOAuth.registrationStatus.restricted",
                    );
                  } else if (status === "OFFLINE") {
                    transformedSettings.registration_status = t(
                      "whatsappOAuth.registrationStatus.offline",
                    );
                  }
                }
                if (transformedSettings.pin_type) {
                  const pinType = transformedSettings.pin_type.toLowerCase();
                  if (pinType === "generated") {
                    transformedSettings.pin_type = t(
                      "whatsappOAuth.pinType.generated",
                    );
                  } else if (pinType === "manual") {
                    transformedSettings.pin_type = t(
                      "whatsappOAuth.pinType.manual",
                    );
                  }
                }
                if (transformedSettings.language_code) {
                  try {
                    const displayName = new Intl.DisplayNames(
                      [locale || "en"],
                      { type: "language" },
                    );
                    const name = displayName.of(
                      transformedSettings.language_code,
                    );
                    if (name) {
                      transformedSettings.language_code = name;
                    }
                  } catch {
                    // Keep the code as-is if Intl fails
                  }
                }

                const displaySettings = {
                  ...transformedSettings,
                  ...(agent.franchise_id && {
                    franchise_id: agentFranchiseDisplayName,
                  }),
                  configured: configuredInfo.value,
                };

                // Filter out hidden fields
                const filteredSettings = Object.fromEntries(
                  Object.entries(displaySettings).filter(
                    ([key]) => !hiddenFields.includes(key),
                  ),
                );

                return Object.keys(filteredSettings).length > 0 ? (
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    {Object.entries(filteredSettings).map(([key, value]) => (
                      <div
                        key={key}
                        className="flex items-center justify-between"
                      >
                        <span className="text-sm text-gray-400 capitalize">
                          {resolveAgentSettingLabel({
                            key,
                            t,
                            locale,
                          })}
                        </span>
                        {key === "configured" && configuredInfo.reason ? (
                          <span className="flex items-center gap-1 text-sm font-medium text-white">
                            {value}
                            <ContextHelp
                              ariaLabel={t(
                                "detail.configuration.configuredHelp",
                              )}
                              whatIs={configuredInfo.reason}
                            />
                          </span>
                        ) : (
                          <span
                            className="max-w-xs truncate text-sm font-medium text-white"
                            title={
                              typeof value === "string"
                                ? value
                                : JSON.stringify(value)
                            }
                          >
                            {typeof value === "string"
                              ? truncateValue(key, value)
                              : truncateValue(key, JSON.stringify(value))}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-400">
                    {t("detail.configuration.empty")}
                  </p>
                );
              })()}
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex space-x-3 border-t border-gray-700 pt-6">
          {isEditing ? (
            <>
              <button
                onClick={() => setIsEditing(false)}
                className="flex-1 rounded-lg bg-gray-600 px-4 py-3 font-medium text-white transition-colors hover:bg-gray-700"
              >
                {t("actions.cancel")}
              </button>
              <button
                onClick={handleSaveChanges}
                disabled={updateAgentMutation.isPending || !hasChanges()}
                className="flex-1 rounded-lg bg-purple-600 px-4 py-3 font-medium text-white transition-colors hover:bg-purple-700 disabled:cursor-not-allowed disabled:bg-gray-600"
              >
                {updateAgentMutation.isPending
                  ? t("actions.saving")
                  : t("actions.saveChanges")}
              </button>
            </>
          ) : (
            <>
              <button
                onClick={() => setIsEditing(true)}
                className="flex-1 rounded-lg bg-purple-600 px-4 py-3 font-medium text-white transition-colors hover:bg-purple-700"
              >
                {t("actions.editAgent")}
              </button>
              {isWhatsApp && (
                <button
                  type="button"
                  onClick={() => setShowTemplatesModal(true)}
                  disabled={!agent?.configured}
                  title={
                    !agent?.configured
                      ? t("templates.disabledNotConfigured")
                      : undefined
                  }
                  className="flex-1 rounded-lg border border-gray-600 bg-gray-800 px-4 py-3 font-medium text-white transition-colors hover:bg-gray-700 disabled:cursor-not-allowed disabled:border-gray-700 disabled:bg-gray-800/50 disabled:text-gray-500 disabled:hover:bg-gray-800/50"
                >
                  {t("templates.viewButton")}
                </button>
              )}
            </>
          )}
        </div>
      </div>

      {/* WhatsApp Templates Modal */}
      {isWhatsApp && (
        <AgentTemplatesModal
          isOpen={showTemplatesModal}
          onClose={() => setShowTemplatesModal(false)}
          agent={agent}
        />
      )}

      {/* Franchise Change Confirmation Modal */}
      {showFranchiseConfirmation && (
        <FranchiseChangeConfirmationModal
          isOpen={showFranchiseConfirmation}
          onClose={handleFranchiseChangeCancelled}
          onConfirm={handleFranchiseChangeConfirmed}
          currentFranchiseName={currentFranchiseConfirmName}
          newFranchiseName={newFranchiseConfirmName}
          isLoading={updateAgentMutation.isPending}
        />
      )}
    </Modal>
  );
};

export default AgentDetailModal;
